// TelcoTrack — RSU occupancy placement, odfOrder, auto trays, fiber assign.

import type { RozvadecMaterialUsageLike } from './rozvadec-cabinet.constants';
import {
  getRayOfsmModule,
  isRayOfsmSap,
  isValidRayTr3StartU,
  rayOfsmConnectorCoord,
  rayOfsmOccupiedUs,
  RAY_TR3_RACK_HEIGHT_U,
  type RayOfsmSap,
} from './rozvadec-ofsm.constants';
import { sumMaterialQuantityBySap } from './rozvadec-weld-capacity';
import { expandPersistedRsuWelds } from './rozvadec-weld-billing';
import {
  RSU_OCCUPANCY_VERSION,
  type RsuOccupancy,
  type RsuOccupancyIssue,
  type RsuOfsmModule,
  type RsuPatchConnection,
  type RsuSplitterPath,
  type RsuTermination,
  type RsuTerminationStatus,
  type RsuWeldConnection,
} from './rozvadec-rsu-occupancy.types';

export function emptyRsuOccupancy(): RsuOccupancy {
  return {
    version: RSU_OCCUPANCY_VERSION,
    modules: [],
    terminations: [],
    weldConnections: [],
    patchConnections: [],
    splitterPaths: [],
  };
}

function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function sortRsuModulesTopToBottom(modules: readonly RsuOfsmModule[]): RsuOfsmModule[] {
  return [...modules].sort((a, b) => a.rackStartU - b.rackStartU || a.odfOrder - b.odfOrder);
}

export function assignRsuOdfOrder(modules: readonly RsuOfsmModule[]): RsuOfsmModule[] {
  return sortRsuModulesTopToBottom(modules).map((module, index) => ({
    ...module,
    odfOrder: index + 1,
  }));
}

export function rsuOccupiedUs(modules: readonly RsuOfsmModule[], exceptId?: string | null): Set<number> {
  const occupied = new Set<number>();
  for (const module of modules) {
    if (exceptId && module.id === exceptId) continue;
    for (const unit of rayOfsmOccupiedUs(module.rackStartU, module.heightU)) {
      occupied.add(unit);
    }
  }
  return occupied;
}

export function rsuModulesOverlap(modules: readonly RsuOfsmModule[]): boolean {
  const seen = new Set<number>();
  for (const module of modules) {
    for (const unit of rayOfsmOccupiedUs(module.rackStartU, module.heightU)) {
      if (seen.has(unit)) return true;
      seen.add(unit);
    }
  }
  return false;
}

export function findFirstFreeRsuSpan(
  modules: readonly RsuOfsmModule[],
  heightU: number,
  exceptId?: string | null,
): number | null {
  if (!Number.isInteger(heightU) || heightU < 1) return null;
  const occupied = rsuOccupiedUs(modules, exceptId);
  for (let start = 1; start <= RAY_TR3_RACK_HEIGHT_U - heightU + 1; start += 1) {
    const span = rayOfsmOccupiedUs(start, heightU);
    if (span.every((unit) => !occupied.has(unit))) return start;
  }
  return null;
}

export function canPlaceRsuModule(
  modules: readonly RsuOfsmModule[],
  startU: number,
  heightU: number,
  exceptId?: string | null,
): boolean {
  if (!isValidRayTr3StartU(startU, heightU)) return false;
  const occupied = rsuOccupiedUs(modules, exceptId);
  return rayOfsmOccupiedUs(startU, heightU).every((unit) => !occupied.has(unit));
}

export function countRsuModulesBySap(modules: readonly RsuOfsmModule[], sapCode: string): number {
  return modules.filter((module) => module.materialSap === sapCode).length;
}

export function rsuMaterialAllowsModule(
  materials: readonly RozvadecMaterialUsageLike[] | null | undefined,
  modules: readonly RsuOfsmModule[],
  sapCode: string,
  exceptId?: string | null,
): boolean {
  const qty = sumMaterialQuantityBySap(materials, sapCode);
  const installed = modules.filter((module) => module.materialSap === sapCode && module.id !== exceptId).length;
  return qty > installed;
}

export type RsuOfsmMaterialShortfall = {
  sapCode: RayOfsmSap;
  nazev: string;
  needed: number;
  have: number;
  missing: number;
};

/** OFSM qty the occupancy still needs on the object (identity rack SAP is not enough). */
export function rsuOfsmMaterialShortfall(
  occupancy: RsuOccupancy,
  materials?: readonly RozvadecMaterialUsageLike[] | null,
): RsuOfsmMaterialShortfall[] {
  const needed = new Map<RayOfsmSap, number>();
  for (const module of occupancy.modules) {
    if (!isRayOfsmSap(module.materialSap)) continue;
    needed.set(module.materialSap, (needed.get(module.materialSap) ?? 0) + 1);
  }
  const rows: RsuOfsmMaterialShortfall[] = [];
  for (const [sapCode, count] of needed) {
    const have = sumMaterialQuantityBySap(materials, sapCode);
    if (count <= have) continue;
    const def = getRayOfsmModule(sapCode);
    rows.push({
      sapCode,
      nazev: def?.nazev ?? sapCode,
      needed: count,
      have,
      missing: count - have,
    });
  }
  return rows;
}

export function placeRsuModule(
  occupancy: RsuOccupancy,
  input: {
    materialSap: string;
    rackStartU?: number | null;
    id?: string | null;
    materials?: readonly RozvadecMaterialUsageLike[] | null;
    requireMaterial?: boolean;
  },
): { occupancy: RsuOccupancy; issue: RsuOccupancyIssue | null } {
  const def = getRayOfsmModule(input.materialSap);
  if (!def || !isRayOfsmSap(input.materialSap)) {
    return {
      occupancy,
      issue: { code: 'MODULE_SAP_INVALID', path: 'modules', message: 'Neznámý SAP modul RAY-OFSM.' },
    };
  }
  if (input.requireMaterial === true && !rsuMaterialAllowsModule(input.materials, occupancy.modules, def.sapCode)) {
    return {
      occupancy,
      issue: {
        code: 'MODULE_MATERIAL_QTY',
        path: 'modules',
        message: `V materiálu objektu chybí volný ${def.sapCode} (${def.nazev}). Osadit z palety lze i tak — množství se doplní při uložení.`,
      },
    };
  }
  const start =
    input.rackStartU != null && Number.isInteger(input.rackStartU)
      ? input.rackStartU
      : findFirstFreeRsuSpan(occupancy.modules, def.heightU);
  if (start == null || !canPlaceRsuModule(occupancy.modules, start, def.heightU)) {
    return {
      occupancy,
      issue: {
        code: 'MODULE_OVERLAP',
        path: 'modules',
        message: 'Pozice se překrývá s jiným modulem nebo ve stojanu není souvislé volné U.',
      },
    };
  }
  const module: RsuOfsmModule = {
    id: (input.id ?? '').trim() || newId('ofsm'),
    materialSap: def.sapCode,
    rackStartU: start,
    heightU: def.heightU,
    odfOrder: 0,
    connectorType: def.connectorType,
  };
  const modules = assignRsuOdfOrder([...occupancy.modules, module]);
  const splitterPaths =
    def.kind === 'splitter'
      ? [...occupancy.splitterPaths, ...emptySplitterPathsFor(module.id, def.splitterCount, def.splitterRatio)]
      : occupancy.splitterPaths;
  return { occupancy: { ...occupancy, modules, splitterPaths }, issue: null };
}

function emptySplitterPathsFor(
  moduleId: string,
  count: number,
  ratio: string | null,
): RsuSplitterPath[] {
  const outputs = ratio === '1:16' ? 16 : ratio === '1:8' ? 8 : 0;
  return Array.from({ length: count }, (_, index) => ({
    id: newId('spl'),
    moduleId,
    splitterIndex: index + 1,
    inputPatchId: null,
    outputPatches: Array.from({ length: outputs }, (__, out) => ({ output: out + 1, patchId: null })),
  }));
}

export function moveRsuModule(
  occupancy: RsuOccupancy,
  moduleId: string,
  rackStartU: number,
): { occupancy: RsuOccupancy; issue: RsuOccupancyIssue | null } {
  const existing = occupancy.modules.find((module) => module.id === moduleId);
  if (!existing) {
    return {
      occupancy,
      issue: { code: 'MODULE_SAP_INVALID', path: 'modules', message: 'Modul nenalezen.' },
    };
  }
  if (!canPlaceRsuModule(occupancy.modules, rackStartU, existing.heightU, existing.id)) {
    return {
      occupancy,
      issue: { code: 'MODULE_OVERLAP', path: 'modules', message: 'Pozice se překrývá s jiným modulem.' },
    };
  }
  const modules = assignRsuOdfOrder(
    occupancy.modules.map((module) =>
      module.id === moduleId ? { ...module, rackStartU } : module,
    ),
  );
  return { occupancy: { ...occupancy, modules }, issue: null };
}

export function removeRsuModule(occupancy: RsuOccupancy, moduleId: string): RsuOccupancy {
  const modules = assignRsuOdfOrder(occupancy.modules.filter((module) => module.id !== moduleId));
  return {
    ...occupancy,
    modules,
    terminations: occupancy.terminations.filter((item) => item.moduleId !== moduleId),
    weldConnections: occupancy.weldConnections.filter((item) => item.moduleId !== moduleId),
    patchConnections: occupancy.patchConnections.filter(
      (item) => !rsuPatchTouchesModule(item, moduleId),
    ),
    splitterPaths: occupancy.splitterPaths.filter((item) => item.moduleId !== moduleId),
  };
}

export function firstFreeRsuConnector(occupancy: RsuOccupancy, moduleId: string): number | null {
  const module = occupancy.modules.find((item) => item.id === moduleId);
  const def = module ? getRayOfsmModule(module.materialSap) : undefined;
  if (!module || !def || def.kind !== 'patch-weld') return null;
  const taken = new Set(
    occupancy.terminations
      .filter((item) => item.moduleId === moduleId && item.status !== 'free')
      .map((item) => item.connectorPosition),
  );
  for (let position = 1; position <= def.connectorCapacity; position += 1) {
    if (!taken.has(position)) return position;
  }
  return null;
}

export function assignRsuFiberRange(
  occupancy: RsuOccupancy,
  input: {
    moduleId: string;
    startPosition: number;
    cableId: string;
    firstPhysicalFiber: number;
    count: number;
    destinationObjectId?: string | null;
    destinationLabel?: string | null;
    status?: RsuTerminationStatus;
  },
): { occupancy: RsuOccupancy; issue: RsuOccupancyIssue | null } {
  const module = occupancy.modules.find((item) => item.id === input.moduleId);
  const def = module ? getRayOfsmModule(module.materialSap) : undefined;
  if (!module || !def || def.kind !== 'patch-weld') {
    return {
      occupancy,
      issue: { code: 'TERMINATION_MODULE_MISSING', path: 'terminations', message: 'Modul pro svár / konektor nebyl nalezen.' },
    };
  }
  if (!Number.isInteger(input.count) || input.count < 1) {
    return {
      occupancy,
      issue: { code: 'TERMINATION_POSITION_INVALID', path: 'terminations', message: 'Rozsah vláken musí být alespoň 1.' },
    };
  }
  const last = input.startPosition + input.count - 1;
  if (input.startPosition < 1 || last > def.connectorCapacity) {
    return {
      occupancy,
      issue: {
        code: 'TERMINATION_POSITION_INVALID',
        path: 'terminations',
        message: `Rozsah konektorů ${input.startPosition}–${last} je mimo 1–${def.connectorCapacity}.`,
      },
    };
  }
  const occupied = new Set(
    occupancy.terminations
      .filter((item) => item.moduleId === input.moduleId && item.status !== 'free')
      .map((item) => item.connectorPosition),
  );
  for (let offset = 0; offset < input.count; offset += 1) {
    if (occupied.has(input.startPosition + offset)) {
      return {
        occupancy,
        issue: {
          code: 'TERMINATION_POSITION_OCCUPIED',
          path: 'terminations',
          message: `Konektor ${input.startPosition + offset} je už obsazený.`,
        },
      };
    }
  }
  const status: RsuTerminationStatus = input.status ?? (input.destinationObjectId ? 'connected' : 'reserve');
  const nextTerms = occupancy.terminations.filter(
    (item) =>
      !(
        item.moduleId === input.moduleId &&
        item.connectorPosition >= input.startPosition &&
        item.connectorPosition <= last
      ),
  );
  const keptWelds = occupancy.weldConnections.filter((weld) =>
    occupancy.terminations.some(
      (term) =>
        term.id === weld.terminationId &&
        !(
          term.moduleId === input.moduleId &&
          term.connectorPosition >= input.startPosition &&
          term.connectorPosition <= last
        ),
    ),
  );
  const terms: RsuTermination[] = [];
  const welds: RsuWeldConnection[] = [];
  for (let offset = 0; offset < input.count; offset += 1) {
    const position = input.startPosition + offset;
    const coord = rayOfsmConnectorCoord(module.materialSap, position);
    if (!coord) continue;
    const terminationId = newId('term');
    terms.push({
      id: terminationId,
      moduleId: module.id,
      connectorPosition: position,
      trayNumber: coord.trayNumber,
      trayWeldPosition: coord.trayWeldPosition,
      cableId: input.cableId,
      physicalFiberNumber: input.firstPhysicalFiber + offset,
      logicalFiberNumber: input.firstPhysicalFiber + offset,
      connectorType: module.connectorType ?? def.connectorType ?? 'SC/APC',
      destinationObjectId: input.destinationObjectId ?? null,
      destinationLabel: input.destinationLabel ?? null,
      status,
    });
    welds.push({
      id: newId('weld'),
      moduleId: module.id,
      terminationId,
      cableId: input.cableId,
      physicalFiberNumber: input.firstPhysicalFiber + offset,
    });
  }
  return {
    occupancy: {
      ...occupancy,
      terminations: [...nextTerms, ...terms],
      weldConnections: [...keptWelds, ...welds],
    },
    issue: null,
  };
}

export function rsuPatchPortModuleId(port: RsuPatchConnection['source']): string | null {
  return port.kind === 'cable-fiber' ? null : port.moduleId;
}

function rsuPatchTouchesModule(row: RsuPatchConnection, moduleId: string): boolean {
  return rsuPatchPortModuleId(row.source) === moduleId || rsuPatchPortModuleId(row.target) === moduleId;
}

export function rsuPatchPortKey(port: RsuPatchConnection['source']): string {
  if (port.kind === 'ofsm-connector') return `pos:${port.moduleId}:${port.connectorPosition}`;
  if (port.kind === 'splitter-in') return `in:${port.moduleId}:${port.splitterIndex}`;
  if (port.kind === 'splitter-out') return `out:${port.moduleId}:${port.splitterIndex}:${port.output}`;
  return `fiber:${port.cableId}#${port.physicalFiber}`;
}

export function findRsuPatchForPort(
  occupancy: RsuOccupancy,
  port: RsuPatchConnection['source'],
): RsuPatchConnection | null {
  const key = rsuPatchPortKey(port);
  return (
    occupancy.patchConnections.find(
      (row) => rsuPatchPortKey(row.source) === key || rsuPatchPortKey(row.target) === key,
    ) ?? null
  );
}

function unbindPatchFromSplitterPaths(
  paths: readonly RsuSplitterPath[],
  patchId: string,
): RsuSplitterPath[] {
  return paths.map((path) => ({
    ...path,
    inputPatchId: path.inputPatchId === patchId ? null : path.inputPatchId,
    outputPatches: path.outputPatches.map((out) =>
      out.patchId === patchId ? { ...out, patchId: null } : out,
    ),
  }));
}

function bindPatchToSplitterPaths(
  paths: readonly RsuSplitterPath[],
  row: RsuPatchConnection,
): RsuSplitterPath[] {
  return paths.map((path) => {
    if (row.source.kind === 'splitter-in' && path.moduleId === row.source.moduleId && path.splitterIndex === row.source.splitterIndex) {
      return { ...path, inputPatchId: row.id };
    }
    if (row.target.kind === 'splitter-in' && path.moduleId === row.target.moduleId && path.splitterIndex === row.target.splitterIndex) {
      return { ...path, inputPatchId: row.id };
    }
    if (row.source.kind === 'splitter-out' && path.moduleId === row.source.moduleId && path.splitterIndex === row.source.splitterIndex) {
      const output = row.source.output;
      return {
        ...path,
        outputPatches: path.outputPatches.map((out) =>
          out.output === output ? { ...out, patchId: row.id } : out,
        ),
      };
    }
    if (row.target.kind === 'splitter-out' && path.moduleId === row.target.moduleId && path.splitterIndex === row.target.splitterIndex) {
      const output = row.target.output;
      return {
        ...path,
        outputPatches: path.outputPatches.map((out) =>
          out.output === output ? { ...out, patchId: row.id } : out,
        ),
      };
    }
    return path;
  });
}

function rsuPatchPortValid(occupancy: RsuOccupancy, port: RsuPatchConnection['source']): boolean {
  if (port.kind === 'cable-fiber') {
    return !!port.cableId.trim() && Number.isInteger(port.physicalFiber) && port.physicalFiber >= 1;
  }
  const module = occupancy.modules.find((item) => item.id === port.moduleId);
  const def = module ? getRayOfsmModule(module.materialSap) : undefined;
  if (!module || !def) return false;
  if (port.kind === 'ofsm-connector') {
    return def.kind === 'patch-weld' && rayOfsmConnectorCoord(module.materialSap, port.connectorPosition) != null;
  }
  if (def.kind !== 'splitter' || port.splitterIndex < 1 || port.splitterIndex > def.splitterCount) return false;
  if (port.kind === 'splitter-in') return true;
  const outputs = def.splitterRatio === '1:16' ? 16 : 8;
  return port.output >= 1 && port.output <= outputs;
}

function rsuPatchPairAllowed(
  source: RsuPatchConnection['source'],
  target: RsuPatchConnection['source'],
): boolean {
  const kinds = new Set([source.kind, target.kind]);
  if (kinds.has('cable-fiber')) {
    return kinds.has('splitter-in') || kinds.has('splitter-out');
  }
  return true;
}

export function addRsuPatchConnection(
  occupancy: RsuOccupancy,
  patch: Omit<RsuPatchConnection, 'id'> & { id?: string },
): RsuOccupancy {
  const row: RsuPatchConnection = { ...patch, id: patch.id?.trim() || newId('patch') };
  return {
    ...occupancy,
    patchConnections: [...occupancy.patchConnections, row],
    splitterPaths: bindPatchToSplitterPaths(occupancy.splitterPaths, row),
  };
}

export function tryAddRsuPatchConnection(
  occupancy: RsuOccupancy,
  patch: Omit<RsuPatchConnection, 'id'> & { id?: string },
): { occupancy: RsuOccupancy; issue: RsuOccupancyIssue | null } {
  if (rsuPatchPortKey(patch.source) === rsuPatchPortKey(patch.target)) {
    return {
      occupancy,
      issue: { code: 'PATCH_PORT_INVALID', path: 'patchConnections', message: 'Zdroj a cíl patchcordu musí být různé.' },
    };
  }
  if (
    !rsuPatchPortValid(occupancy, patch.source)
    || !rsuPatchPortValid(occupancy, patch.target)
    || !rsuPatchPairAllowed(patch.source, patch.target)
  ) {
    return {
      occupancy,
      issue: { code: 'PATCH_PORT_INVALID', path: 'patchConnections', message: 'Port patchcordu je mimo modul.' },
    };
  }
  if (findRsuPatchForPort(occupancy, patch.source) || findRsuPatchForPort(occupancy, patch.target)) {
    return {
      occupancy,
      issue: {
        code: 'PATCH_PORT_INVALID',
        path: 'patchConnections',
        message: 'Port už má patchcord. Nejdřív ho odpojte.',
      },
    };
  }
  return { occupancy: addRsuPatchConnection(occupancy, patch), issue: null };
}

export function removeRsuPatchConnection(occupancy: RsuOccupancy, patchId: string): RsuOccupancy {
  const id = patchId.trim();
  if (!id) return occupancy;
  return {
    ...occupancy,
    patchConnections: occupancy.patchConnections.filter((item) => item.id !== id),
    splitterPaths: unbindPatchFromSplitterPaths(occupancy.splitterPaths, id),
  };
}

/** Drop one OFSM termination and its weld; related front patches go with it. */
export function removeRsuTermination(occupancy: RsuOccupancy, terminationId: string): RsuOccupancy {
  const term = occupancy.terminations.find((item) => item.id === terminationId);
  if (!term) return occupancy;
  const patchIds = new Set(
    occupancy.patchConnections
      .filter((row) => {
        const ports = [row.source, row.target];
        return ports.some(
          (port) =>
            port.kind === 'ofsm-connector' &&
            port.moduleId === term.moduleId &&
            port.connectorPosition === term.connectorPosition,
        );
      })
      .map((row) => row.id),
  );
  let next: RsuOccupancy = {
    ...occupancy,
    terminations: occupancy.terminations.filter((item) => item.id !== terminationId),
    weldConnections: occupancy.weldConnections.filter((item) => item.terminationId !== terminationId),
  };
  for (const patchId of patchIds) {
    next = removeRsuPatchConnection(next, patchId);
  }
  return next;
}

export function formatRsuD5Address(input: {
  stojanLabel: string;
  odfOrder: number;
  connectorPosition: number;
}): string {
  const stojan = input.stojanLabel.trim() || 'RAY-TR3';
  return `${stojan} / ODF ${input.odfOrder} / pozice ${input.connectorPosition}`;
}

export function rsuStatusLabelCs(status: RsuTerminationStatus): string {
  if (status === 'connected') return 'provoz';
  if (status === 'reserve') return 'rezerva';
  return 'volná';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function field(raw: Record<string, unknown>, key: string): unknown {
  return raw[key];
}

export function readRsuOccupancy(value: unknown): RsuOccupancy | null {
  if (!isRecord(value) || field(value, 'version') !== RSU_OCCUPANCY_VERSION) return null;
  const modules = field(value, 'modules');
  if (!Array.isArray(modules)) return null;
  const terminationsRaw = field(value, 'terminations');
  const welds = field(value, 'weldConnections');
  const patches = field(value, 'patchConnections');
  const paths = field(value, 'splitterPaths');
  const terminations = Array.isArray(terminationsRaw)
    ? terminationsRaw.filter(isRecord).map(readTermination).filter((item): item is RsuTermination => item != null)
    : [];
  return {
    version: RSU_OCCUPANCY_VERSION,
    modules: modules.filter(isRecord).map(readModule).filter((item): item is RsuOfsmModule => item != null),
    terminations,
    weldConnections: expandPersistedRsuWelds(
      Array.isArray(welds)
        ? welds.filter(isRecord).map(readWeld).filter((item): item is RsuWeldConnection => item != null)
        : [],
      terminations,
    ),
    patchConnections: Array.isArray(patches)
      ? patches.filter(isRecord).map(readPatch).filter((item): item is RsuPatchConnection => item != null)
      : [],
    splitterPaths: Array.isArray(paths)
      ? paths.filter(isRecord).map(readSplitterPath).filter((item): item is RsuSplitterPath => item != null)
      : [],
  };
}

function asInt(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    if (Number.isInteger(parsed)) return parsed;
  }
  return null;
}

function readModule(raw: Record<string, unknown>): RsuOfsmModule | null {
  const sap = String(field(raw, 'materialSap') ?? '').trim();
  const def = getRayOfsmModule(sap);
  const start = asInt(field(raw, 'rackStartU'));
  const height = asInt(field(raw, 'heightU'));
  if (!def || !isRayOfsmSap(sap) || start == null || height == null) return null;
  return {
    id: String(field(raw, 'id') ?? '').trim() || newId('ofsm'),
    materialSap: sap,
    rackStartU: start,
    heightU: (height === 2 || height === 3 ? height : 1) as 1 | 2 | 3,
    odfOrder: asInt(field(raw, 'odfOrder')) ?? 0,
    connectorType: def.connectorType,
  };
}

function readTermination(raw: Record<string, unknown>): RsuTermination | null {
  const moduleId = String(field(raw, 'moduleId') ?? '').trim();
  const position = asInt(field(raw, 'connectorPosition'));
  if (!moduleId || position == null) return null;
  const statusRaw = String(field(raw, 'status') ?? 'free');
  const status: RsuTerminationStatus =
    statusRaw === 'connected' || statusRaw === 'reserve' ? statusRaw : 'free';
  return {
    id: String(field(raw, 'id') ?? '').trim() || newId('term'),
    moduleId,
    connectorPosition: position,
    trayNumber: asInt(field(raw, 'trayNumber')) ?? 1,
    trayWeldPosition: asInt(field(raw, 'trayWeldPosition')) ?? 1,
    cableId: field(raw, 'cableId') == null ? null : String(field(raw, 'cableId')),
    physicalFiberNumber: asInt(field(raw, 'physicalFiberNumber')),
    logicalFiberNumber: asInt(field(raw, 'logicalFiberNumber')),
    connectorType: String(field(raw, 'connectorType') ?? 'SC/APC'),
    destinationObjectId: field(raw, 'destinationObjectId') == null ? null : String(field(raw, 'destinationObjectId')),
    destinationLabel: field(raw, 'destinationLabel') == null ? null : String(field(raw, 'destinationLabel')),
    status,
  };
}

function readWeld(raw: Record<string, unknown>): RsuWeldConnection | null {
  const moduleId = String(field(raw, 'moduleId') ?? '').trim();
  const terminationId = String(field(raw, 'terminationId') ?? '').trim();
  const cableId = String(field(raw, 'cableId') ?? '').trim();
  const fiber = asInt(field(raw, 'physicalFiberNumber'));
  if (!moduleId || !terminationId || !cableId || fiber == null) return null;
  const rangeRaw = field(raw, 'fiberRange');
  const rangeFrom = isRecord(rangeRaw) ? asInt(field(rangeRaw, 'from')) : null;
  const rangeTo = isRecord(rangeRaw) ? asInt(field(rangeRaw, 'to')) : null;
  const ops = asInt(field(raw, 'physicalWeldOperationCount'));
  const spliced = asInt(field(raw, 'splicedFiberCount'));
  return {
    id: String(field(raw, 'id') ?? '').trim() || newId('weld'),
    moduleId,
    terminationId,
    cableId,
    physicalFiberNumber: fiber,
    ...(rangeFrom != null && rangeTo != null && rangeTo >= rangeFrom
      ? { fiberRange: { from: rangeFrom, to: rangeTo } }
      : {}),
    ...(ops != null && ops >= 1 ? { physicalWeldOperationCount: ops } : {}),
    ...(spliced != null && spliced >= 1 ? { splicedFiberCount: spliced } : {}),
  };
}

function readPatchPort(raw: unknown): RsuPatchConnection['source'] | null {
  if (!isRecord(raw)) return null;
  const kind = String(field(raw, 'kind') ?? '');
  if (kind === 'cable-fiber') {
    const cableId = String(field(raw, 'cableId') ?? '').trim();
    const fiber = asInt(field(raw, 'physicalFiber'));
    if (!cableId || fiber == null || fiber < 1) return null;
    return { kind, cableId, physicalFiber: fiber };
  }
  const moduleId = String(field(raw, 'moduleId') ?? '').trim();
  if (!moduleId) return null;
  if (kind === 'ofsm-connector') {
    const position = asInt(field(raw, 'connectorPosition'));
    if (position == null) return null;
    return { kind, moduleId, connectorPosition: position };
  }
  const splitterIndex = asInt(field(raw, 'splitterIndex'));
  if (splitterIndex == null) return null;
  if (kind === 'splitter-in') return { kind, moduleId, splitterIndex };
  if (kind === 'splitter-out') {
    const output = asInt(field(raw, 'output'));
    if (output == null) return null;
    return { kind, moduleId, splitterIndex, output };
  }
  return null;
}

function readPatch(raw: Record<string, unknown>): RsuPatchConnection | null {
  const source = readPatchPort(field(raw, 'source'));
  const target = readPatchPort(field(raw, 'target'));
  if (!source || !target) return null;
  return { id: String(field(raw, 'id') ?? '').trim() || newId('patch'), source, target };
}

function readSplitterPath(raw: Record<string, unknown>): RsuSplitterPath | null {
  const moduleId = String(field(raw, 'moduleId') ?? '').trim();
  const splitterIndex = asInt(field(raw, 'splitterIndex'));
  if (!moduleId || splitterIndex == null) return null;
  const outputPatches = field(raw, 'outputPatches');
  const outputs = Array.isArray(outputPatches)
    ? outputPatches
        .filter(isRecord)
        .map((item) => {
          const output = asInt(field(item, 'output'));
          if (output == null) return null;
          const patchId = field(item, 'patchId');
          return { output, patchId: patchId == null ? null : String(patchId) };
        })
        .filter((item): item is { output: number; patchId: string | null } => item != null)
    : [];
  const inputPatchId = field(raw, 'inputPatchId');
  return {
    id: String(field(raw, 'id') ?? '').trim() || newId('spl'),
    moduleId,
    splitterIndex,
    inputPatchId: inputPatchId == null ? null : String(inputPatchId),
    outputPatches: outputs,
  };
}

export function validateRsuOccupancy(
  occupancy: RsuOccupancy,
  materials?: readonly RozvadecMaterialUsageLike[] | null,
  options?: { requireMaterial?: boolean },
): RsuOccupancyIssue[] {
  const issues: RsuOccupancyIssue[] = [];
  if (occupancy.version !== RSU_OCCUPANCY_VERSION) {
    issues.push({ code: 'VERSION_UNSUPPORTED', path: 'version', message: 'Nepodporovaná verze osazení RAY-TR3.' });
  }
  if (rsuModulesOverlap(occupancy.modules)) {
    issues.push({
      code: 'MODULE_OVERLAP',
      path: 'modules',
      message: 'Osazené moduly RAY-OFSM se v 42U stojanu překrývají.',
    });
  }
  const qtyBySap = new Map<RayOfsmSap, number>();
  for (const module of occupancy.modules) {
    const def = getRayOfsmModule(module.materialSap);
    if (!def) {
      issues.push({ code: 'MODULE_SAP_INVALID', path: `modules.${module.id}`, message: 'Neznámý SAP modul RAY-OFSM.' });
      continue;
    }
    if (!isValidRayTr3StartU(module.rackStartU, module.heightU) || module.heightU !== def.heightU) {
      issues.push({
        code: 'MODULE_SPAN_INVALID',
        path: `modules.${module.id}`,
        message: 'Rozpětí U je mimo 1–42 nebo neodpovídá výšce SAP.',
      });
    }
    qtyBySap.set(module.materialSap, (qtyBySap.get(module.materialSap) ?? 0) + 1);
  }
  if (options?.requireMaterial === true && materials) {
    for (const [sap, count] of qtyBySap) {
      if (count > sumMaterialQuantityBySap(materials, sap)) {
        const def = getRayOfsmModule(sap);
        issues.push({
          code: 'MODULE_MATERIAL_QTY',
          path: `modules.${sap}`,
          message: `V materiálu objektu chybí volný ${sap} (${def?.nazev ?? sap}).`,
        });
      }
    }
  }
  const moduleIds = new Set(occupancy.modules.map((module) => module.id));
  const termKeys = new Set<string>();
  for (const term of occupancy.terminations) {
    if (!moduleIds.has(term.moduleId)) {
      issues.push({
        code: 'TERMINATION_MODULE_MISSING',
        path: `terminations.${term.id}`,
        message: 'Ukončení odkazuje na chybějící modul.',
      });
      continue;
    }
    const module = occupancy.modules.find((item) => item.id === term.moduleId);
    const coord = module ? rayOfsmConnectorCoord(module.materialSap, term.connectorPosition) : null;
    if (!coord) {
      issues.push({
        code: 'TERMINATION_POSITION_INVALID',
        path: `terminations.${term.id}`,
        message: 'Pozice konektoru je mimo pole modulu.',
      });
    }
    const key = `${term.moduleId}#${term.connectorPosition}`;
    if (termKeys.has(key) && term.status !== 'free') {
      issues.push({
        code: 'TERMINATION_POSITION_OCCUPIED',
        path: `terminations.${term.id}`,
        message: 'Dvě ukončení sdílejí stejný konektor.',
      });
    }
    if (term.status !== 'free') termKeys.add(key);
  }
  return issues;
}

export type {
  RsuOccupancy,
  RsuOfsmModule,
  RsuTermination,
  RsuWeldConnection,
  RsuPatchConnection,
  RsuSplitterPath,
  RsuTerminationStatus,
  RsuOccupancyIssue,
  RsuOccupancyIssueCode,
} from './rozvadec-rsu-occupancy.types';
export type { RayOfsmSap };
